import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storiesApi } from '../../services/api';

// Async thunks
export const fetchStories = createAsyncThunk(
    'stories/fetchAll',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await storiesApi.getAll(params);
            return {
                items: response.data.data,
                pagination: response.data.pagination,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch stories');
        }
    }
);

export const createStory = createAsyncThunk(
    'stories/create',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await storiesApi.create(formData);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create story');
        }
    }
);

export const updateStory = createAsyncThunk(
    'stories/update',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await storiesApi.update(id, formData);
            return { id, ...response.data.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update story');
        }
    }
);

export const patchStory = createAsyncThunk(
    'stories/patch',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await storiesApi.patch(id, data);
            return { id, ...data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update story');
        }
    }
);

export const deleteStory = createAsyncThunk(
    'stories/delete',
    async (id, { rejectWithValue }) => {
        try {
            await storiesApi.delete(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete story');
        }
    }
);

const initialState = {
    items: [],
    loading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
    },
    filters: {
        search: '',
        category_id: null,
    },
    selectedStory: null,
};

const storiesSlice = createSlice({
    name: 'stories',
    initialState,
    reducers: {
        setSelectedStory: (state, action) => {
            state.selectedStory = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        reorderStories: (state, action) => {
            state.items = action.payload;
        },
        toggleField: (state, action) => {
            const { id, field } = action.payload;
            const item = state.items.find(i => i.id === id);
            if (item) {
                item[field] = !item[field];
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all
            .addCase(fetchStories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStories.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items || [];
                if (action.payload.pagination) {
                    state.pagination = action.payload.pagination;
                }
            })
            .addCase(fetchStories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create
            .addCase(createStory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createStory.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.items.unshift(action.payload);
                }
            })
            .addCase(createStory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update
            .addCase(updateStory.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...action.payload };
                }
            })
            // Patch
            .addCase(patchStory.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...action.payload };
                }
            })
            // Delete
            .addCase(deleteStory.fulfilled, (state, action) => {
                state.items = state.items.filter(item => item.id !== action.payload);
                state.pagination.total = Math.max(0, state.pagination.total - 1);
            });
    },
});

export const { setSelectedStory, setFilters, setPage, clearError, reorderStories, toggleField } = storiesSlice.actions;
export default storiesSlice.reducer;
